import { Injectable, OnDestroy } from '@angular/core';

import { MessageService } from 'primeng/api';
import { Observable, forkJoin, BehaviorSubject, of } from 'rxjs';
import { tap, catchError, filter, defaultIfEmpty, concatMap, switchMap } from 'rxjs/operators';
import { find } from 'lodash';

import { DataConnectorsApi } from '@api/externalData/data-connectors.api';
import { SearchCriteriaModel } from '@api/mentions/model/search-criteria.model';
import { SavedMentionSearchesApi } from '@api/mentions/saved-mention-searches.api';
import { SavedMentionSearchApiModel } from '@api/mentions/model/saved-mention-search.api.model';
import { MentionsApi } from '@api/mentions/mentions.api';
import { ExternalDataApi } from '@api/externalData/external-data.api';

import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';
import { BriefingsService } from './briefings.service';
import { BriefingModel } from '../models/briefing.model';
import { MentionSearchModel } from '@Monitor/Shared/models/mention-search.model';
import { ReportsSeriesService } from '@Common/Reports/services/reports-series.service';
import { ReportsSeriesMapperService } from '@Common/Reports/services/reports-series-mapper.service';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { ReportsExternalDataMapperService } from '@Common/Reports/services/report-external-data-mapper.service';
import { IndexeddbCoreCacheService } from '@Core/Cache/services/indexeddb-core-cache.service';
import { MediaSubscriptionService } from '@Monitor/Core/services/media-subscription.service';
import { ThemeService } from '@Social/Intelligence/services/theme.service';
import { CurrencyConversionService } from '@Core/Users/services/currency-conversion.service';
import { DataConnectionService } from '@Core/Users/services/data-connection.service';
import { ReportsSeriesDataMapperService } from '@Common/Reports/services/reports-series-data-mapper.service';
import { DataConnectorTypesEnum } from '@api/externalData/enums/data-connector-types.enum';
import { ReportBlockTypeEnum } from '@Common/Reports/enums/report-block-type.enum';
import { MentionsSearchResponseModel } from '@api/mentions/model/mentions-search-response.model';
import { BlockModel } from '@Common/PageBuilder/models/block.model';
import { BriefingChartDisplayElementModel } from '@Common/PageBuilder/models/briefing-chart-display-element.model';
import { ChartDisplayElementModel } from '@Common/Reports/models/chart-display-element.model';
import { LayoutBlock } from '@Common/PageBuilder/models/layout-block.model';
import { BriefingBlockTypeEnum } from '../enums/briefing-block-type.enum';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { ReportDateRangeModel } from '../../../Common/Reports/models/report-date-range.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { AgentService } from '@Social/Intelligence/services/agent.service';

/**
 * Responsible for;
 * - parsing array(s) of mention series
 * - determine which of those series share mention searches
 * - requesting mention data in the fewest requests possible
 * - cache that data for a certain period of time
 */
@Injectable()
export class BriefingSeriesService extends ReportsSeriesService implements OnDestroy {

    private currentBriefing: BriefingModel;

    readonly googleAnalyticsTypes = [DataConnectorTypesEnum.GOOGLE_ANALYTICS, DataConnectorTypesEnum.GOOGLE_ANALYTICS_4];

    constructor(
        public briefingsService: BriefingsService,
        public savedSearchesApi: SavedMentionSearchesApi,
        public mentionsApi: MentionsApi,
        public reportsSeriesMapperService: ReportsSeriesMapperService,
        public localizationService: LocalizationService,
        public externalDataMapperService: ReportsExternalDataMapperService,
        public messageService: MessageService,
        public currentUserStateService: CurrentUserStateService,
        public externalDataApi: ExternalDataApi,
        public indexedDbCoreCacheService: IndexeddbCoreCacheService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public themeService: ThemeService,
        public currencyConversionService: CurrencyConversionService,
        public dataConnectionService: DataConnectionService,
        public reportsSeriesDataMapperService: ReportsSeriesDataMapperService,
        public dataConnectorsApiService: DataConnectorsApi,
        public agentService: AgentService
    ) {
        super(
            savedSearchesApi,
            mentionsApi,
            reportsSeriesMapperService,
            localizationService,
            null,
            externalDataMapperService,
            messageService,
            currentUserStateService,
            externalDataApi,
            indexedDbCoreCacheService,
            mediaSubscriptionService,
            themeService,
            currencyConversionService,
            dataConnectionService,
            reportsSeriesDataMapperService,
            dataConnectorsApiService,
            agentService
        );
        this.subscriptions.push(
            this.briefingsService.getFocusedResource().pipe(
                filter(x => !!x),
                tap(currentBriefing => this.currentBriefing = currentBriefing),
                switchMap(currentBriefing => {
                    // for canvas generate now charts, we store the mention response data in B.E and should use them directly instead of fetch them again
                    const blocksUsingExistingData = [];
                    currentBriefing.blocks.forEach(block => {
                        if (block.isLayoutBlockType()) {
                            (block as LayoutBlock).blocks.forEach(column => {
                                column.forEach(x => {
                                    if (x.type.key === BriefingBlockTypeEnum.CHART &&
                                        !(x.displayElement as BriefingChartDisplayElementModel).chart.generateOnSend) {
                                        blocksUsingExistingData.push(x);
                                    }
                                });
                            });
                        } else {
                            if (block.type.key === BriefingBlockTypeEnum.CHART &&
                                !(block.displayElement as BriefingChartDisplayElementModel).chart.generateOnSend) {
                                blocksUsingExistingData.push(block);
                            }
                        }
                    });


                    blocksUsingExistingData.forEach(block => {
                        const chartDisplayElement =
                            (block.displayElement as BriefingChartDisplayElementModel)?.chartBlock?.displayElement as ChartDisplayElementModel;
                        chartDisplayElement?.series?.forEach(series => {
                            const data = series.dataProvider?.getValue();
                            if (!data && series.searchResults?.currentPeriodResults) {
                                series.mentions.isPreviousPeriod = false;
                                this.mapMentionFacetResults(series,
                                    series.toSearchCriteriaModel(),
                                    series.searchResults?.currentPeriodResults
                                );
                            }
                            const previousData = series.previousPeriodDataProvider?.getValue();
                            if (!previousData && series.searchResults?.previousPeriodResults && series.dateRange.type !== DateQueryTypeEnum.ALL) {
                                const previousSeries = series.clone();
                                previousSeries.mentions.isPreviousPeriod = true;
                                const previousDateRange = new ReportDateRangeModel()
                                    .setFromDateQuery(DateQueryModel.getPreviousPeriod(previousSeries.dateRange as DateQueryModel));
                                previousSeries.dateRange = previousDateRange;
                                previousSeries.previousPeriodDataProvider = series.previousPeriodDataProvider;

                                this.mapMentionFacetResults(previousSeries,
                                    previousSeries.toSearchCriteriaModel(),
                                    series.searchResults?.previousPeriodResults);
                            }
                        });
                    });

                    if (currentBriefing.getChartExternalDataSeries().length > 0) {
                        return this.dataConnectionService?.googleAnalyticsDataConnectors$.pipe(
                            filter(x => !!x),
                            tap(connectors => this.googleAnalyticsDataConnectors = connectors)
                        );
                    } else {
                        return of(null);
                    }
                }),
                switchMap(() => this.resetSeries(this.currentBriefing.getChartBlocksWithoutData())),
                switchMap(() => this.prepareSeries(this.currentBriefing.getChartBlocksWithoutData())),
            ).subscribe(() => {
                this.checkIfShouldSetGlobalState();
            })
        );
    }

    async resetSeries(requestedBlocks: BlockModel[]): Promise<void> {
        this.mentionDataProvidersMap.clear();
        this.mentionDataMap.clear();

        this.externalDataMap.clear();
        this.externalDataProvidersMap.clear();

        if (requestedBlocks?.length) {
            for (const requestedBlock of requestedBlocks) {
                if (!requestedBlock) {
                    continue;
                }

                let series = [];
                if (requestedBlock.isLayoutBlockType()) {
                    (requestedBlock as LayoutBlock).blocks.forEach(column => {
                        column.forEach(x => {
                            if (x.type.key === BriefingBlockTypeEnum.CHART) {
                                series.push(
                                    ...((x.displayElement as BriefingChartDisplayElementModel)
                                        .chartBlock.displayElement as ChartDisplayElementModel).series
                                );
                            }
                        });
                    });
                } else {
                    series = ((requestedBlock.displayElement as BriefingChartDisplayElementModel)
                        .chartBlock?.displayElement as ChartDisplayElementModel)?.series || [];
                }

                for (const s of series) {
                    if (s.mentions) {
                        s.mentions.topic = null;
                        s.mentions.mediaSubscription = null;
                        s.mentions.socialTopic = null;
                        s.mentions.theme = null;
                    }

                    // we also have to clear the previous period from index db
                    if (s.hasPreviousPeriod) {
                        const previousPeriodSeries: ReportSeriesModel = s.clone();
                        previousPeriodSeries.hasPreviousPeriod = false;
                        previousPeriodSeries.mentions.isPreviousPeriod = true;
                        previousPeriodSeries.previousPeriodDataProvider = s.previousPeriodDataProvider;
                        await this.indexedDbCoreCacheService.del(previousPeriodSeries.getSeriesKey());
                    }

                    await this.indexedDbCoreCacheService.del(s.getSeriesKey());
                }
            }
        }
    }

    /**
     * Loop through all the available series of the current Briefing;
     * - Populating the `searchSeriesMap`, which is used to map searches to series, which reduces duplicate calls.
     * - Populating the `mentionDataMap`, with existing series data for cases that don't require searches.
     * - Populating the `externalDataMap`, with google analytics data
     *
     * Topic mention series is a special case that requires the search object to be fetched before it can be added to `searchSeriesMap`.
     * Series that are apart of a shared report should not make api calls.
     *
     * Perform facet searches, constructed by `prepareSearch()`, and forkJoin'd
     */
    prepareSeries(requestedBlocks: BlockModel[]): Observable<MentionsSearchResponseModel[]> {
        this.mentionDataProvidersMap.clear();
        this.mentionDataMap.clear();

        this.externalDataMap.clear();
        this.externalDataProvidersMap.clear();

        const searchSeriesMap = new Map<SearchCriteriaModel, ReportSeriesModel[]>();
        const topicRequestMap = new Map<ReportSeriesModel, Observable<SavedMentionSearchApiModel>>();

        for (const requestedBlock of requestedBlocks) {
            for (const series of this.currentBriefing.getChartMentionsSeries(requestedBlock)) {
                if (series.mentions && !series.isSharedReport && !series.styles?.ignoreApiCall) {

                    // topic search object does not exist - request the search below
                    if (series.mentions.topicId && !series.mentions.topic) {
                        topicRequestMap.set(series, this.savedSearchesApi.getById(series.mentions.topicId));
                        continue;
                    }

                    // handle missing media subscription search
                    if (series.mentions.mediaSubscriptionId && !series.mentions.mediaSubscription) {
                        const mentionSearchModel = new MentionSearchModel();
                        mentionSearchModel.searchCriteria.query.subscription.include = [series.mentions.mediaSubscriptionId];
                        series.mentions.mediaSubscription = mentionSearchModel;
                    }

                    this.addToSearchMap(searchSeriesMap, series.toSearchCriteriaModel(), series);
                } else if (series.styles?.ignoreApiCall) {
                    const seriesKey = series.getSeriesKey();
                    if (!this.mentionDataProvidersMap.get(seriesKey)) {
                        this.mentionDataMap.set(seriesKey, null);
                    }
                }
            }

            for (const series of this.currentBriefing.getChartSocialSeries(requestedBlock)) {
                if (series.mentions && !series.isSharedReport && !series.styles?.ignoreApiCall) {
                    if (series.mentions.socialTopicId && !series.mentions.socialTopic) {
                        const mentionSearchModel = new MentionSearchModel();
                        mentionSearchModel.searchCriteria.query.agent.include = [series.mentions.socialTopicId];
                        series.mentions.socialTopic = mentionSearchModel;
                        series.styles.socialTopicThemeIds = find(this.agents, {id: series.mentions.socialTopicId})?.themeIds;
                    } else if (series.mentions.themeId && !series.mentions.theme) {
                        const mentionSearchModel = new MentionSearchModel();
                        mentionSearchModel.searchCriteria.query.theme.include = [series.mentions.themeId];
                        series.mentions.theme = mentionSearchModel;
                        series.styles.socialTopicIds = find(this.socialThemes, {id: series.mentions.themeId})?.subscriptionIds;
                    }

                    this.addToSearchMap(searchSeriesMap, series.toSearchCriteriaModel(), series);
                } else if (series.styles?.ignoreApiCall) {
                    const seriesKey = series.getSeriesKey();
                    if (!this.mentionDataProvidersMap.get(seriesKey)) {
                        this.mentionDataMap.set(seriesKey, null);
                    }
                }
            }

            for (const series of this.currentBriefing.getChartExternalDataSeries(requestedBlock)) {
                if (!series.isSharedReport && this.googleAnalyticsTypes.includes(series.externalData.externalDataType)) {

                    if (this.validateGoogleAnalyticsSeries(series)) {
                        // todo: deal with async await later, as GA is not in scope for 7-1 anyway
                        // await this.handleExternalSeriesBackwardCompatibility(series);
                        this.loadDataForExternalGoogleAnalyticsSeries(series);
                    } else {
                        const seriesKey = series.getSeriesKey();
                        if (!this.externalDataProvidersMap.get(seriesKey)) {
                            // The facetDataMap is empty for this series key, fetch the data for it
                            this.mentionDataMap.set(seriesKey, null);
                            this.externalDataProvidersMap.set(seriesKey, new BehaviorSubject(null));
                        }

                        const errorString = this.localizationService.transform('Reports.error.externalDataSourceRemoved');
                        this.externalDataProvidersMap.get(seriesKey).error(new Error(errorString));
                        series.mentions.topicId = null;
                        series.dataProvider.error(new Error(errorString));
                        this.hasBadData = true;
                        this.checkIfShouldSetGlobalState();
                    }
                }
            }
        }

        // construct saved topic search object requests
        const savedMentionSearches: Observable<SavedMentionSearchApiModel>[] = [];
        const missingDataSources = [];
        for (const [series, search] of topicRequestMap.entries()) {
            savedMentionSearches.push(
                search.pipe(
                    tap(response => this.setSeriesSearches(series, [response])),
                    tap(() => {
                        this.addToSearchMap(searchSeriesMap, series.toSearchCriteriaModel(), series);
                    }),
                    catchError(error => {
                        if (error.status === 404) {
                            // Topic or subscription no longer exists
                            const seriesKey = series.getSeriesKey();
                            if (!this.mentionDataProvidersMap.get(seriesKey)) {
                                // The facetDataMap is empty for this series key, fetch the data for it
                                this.mentionDataMap.set(seriesKey, null);
                                this.mentionDataProvidersMap.set(seriesKey, new BehaviorSubject(null));
                            }

                            const errorString = this.localizationService.transform('Reports.error.topicRemoved');
                            this.mentionDataProvidersMap.get(seriesKey).error(new Error(errorString));
                            series.dataProvider.error(new Error(errorString));
                            missingDataSources.push(series);
                            this.checkIfShouldSetGlobalState();
                        }
                        return of(null);
                    })
                )
            );
        }

        // request saved search object
        return forkJoin(savedMentionSearches).
            pipe(
                defaultIfEmpty(null),
                concatMap(async (_) => {
                    const facetSearches: Observable<MentionsSearchResponseModel>[] = [];
                    this.briefingsService.setBlockNoData(missingDataSources);

                    for (const [searchCriteria, seriesList] of searchSeriesMap.entries()) {
                        const isFacetSearch = seriesList[0].chartType !== ReportBlockTypeEnum.TOP_MENTIONS;
                        const request = await this.prepareSearch(seriesList, searchCriteria, isFacetSearch);
                        if (request) {
                            facetSearches.push(request);
                        }
                    }

                    return forkJoin(facetSearches);
                }),
                concatMap(v => {
                    return v;
                })
            );
    }


    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
